import React, { useState, useRef, useEffect } from "react";
import "./style.scss";
import MaleUser from "../../../assets/MaleUser.svg";
import FemaleUser from "../../../assets/FemaleUser.svg";
import BusinessUser from "../../../assets/BusinessUser.svg";
import EditImg from "../../../assets/EditImg.svg";
import { ReactComponent as UserIcon } from "../../../assets/icon-user.svg";
import { ReactComponent as PasswordIcon } from "../../../assets/icon-unlock.svg";
import { ReactComponent as TaxInformationIcon } from "../../../assets/icon-tax.svg";
import { ReactComponent as VerificationIcon } from "../../../assets/icon-onboarding.svg";
import { ReactComponent as IconQuestion } from "../../../assets/IconQuestion.svg";
import { ReactComponent as PromoCode } from "../../../assets/icon-promocode.svg";
import { ReactComponent as MyBonuses } from "../../../assets/icon-bonus.svg";
import { ReactComponent as LanguageIcon } from "../../../assets/icon-globe.svg";
import { ReactComponent as WithdrawalIcon } from "../../../assets/icon-arrow-up-circle.svg";
import { ReactComponent as DepositIcon } from "../../../assets/icon-arrow-down-circle.svg";
import { ReactComponent as HistoryIcon } from "../../../assets/icon-clock.svg";
import { ReactComponent as TextDocIcon } from "../../../assets/icon-file.svg";
import { ReactComponent as RepoIcon } from "../../../assets/icon-repo-transaction.svg";
import { ReactComponent as AssignmentsIcon } from "../../../assets/icon-assignments.svg";
import IconCamera from "../../../assets/IconCamera.png";
import { UserStatus } from "../components/account-status";
import { AccountOptionCard } from "../components/account-option-card";
import { EditProfilePhoto } from "../components/edit-profile-photo";
import { useLocation, useNavigate } from "react-router-dom";
import { PATH_LIST } from "shared/lib/react-router";
import { LanguageModal } from "../components/language-modal";
import { useStore } from "zustand";
import { blobToFile, getCdnUrl, showNotification } from "helpers/functions";
import {
  accountStore,
  KYB_Step,
  KYC_Status,
  KYC_Step,
  useUserKYCStatus,
} from "entities/account";
import { useTranslation } from "react-i18next";
import TakePhotoModal from "pages/app/verification-steps/components/take-photo-modal";
import { DocumentType, EntityType } from "static/types";
import { useBlockScrollbar } from "hooks/useBlockScrollbar";
import {
  sessionStore,
  UserVerifyStatus,
  useUserName,
} from "entities/session/model/sessionModel";
import { PromoCodeModal } from "../components/promo-code-modal";
import Modal from "widgets/auth/modal";
import { useVerificationNavigation } from "hooks/useNavigateVerification";
import VerificationRequest from "../../../assets/verification/VerificationRequest.svg";
import { SubAccountModal } from "../components/sub-account-modal";
import useSubAccount from "hooks/useSubAccount";

export const AccountPage = () => {
  const [editImgModal, setEditImgModal] = useState(false);
  const [languageModal, setLanguageModal] = useState(false);
  const [subAccountsModal, setSubAccountsModal] = useState(false);
  const [promoCodeModal, setPromoCodeModal] = useState(false);
  const [video, setVideo] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const {
    user,
    entity,
    subAccountSubNumber,
    setSubAccountSubNumber,
    updateUserAvatar,
    setInactiveEmailModal,
    setInactivePinCodeModal,
    crsData,
  } = useStore(sessionStore);
  const { subAccount } = useSubAccount();
  const username = useUserName();
  const { t: err } = useTranslation("errors");
  const { t } = useTranslation("app");
  const { t: v } = useTranslation("verification");
  const { t: o } = useTranslation("onboarding");
  const {
    uploadAvatar,
    removeAvatar,
    kyc,
    kyb,
    questionnaireStatus,
    depositPaymentsCount,
    getDepositPayments,
    repoDeals,
    repoDealsHistory,
    getRepoDeals,
    subAccounts,
    getSubAccounts,
  } = useStore(accountStore);
  const navigate = useNavigate();
  const kycbStatus = useUserKYCStatus();
  const isLegal = entity === EntityType.Entity;
  const location = useLocation();

  useEffect(() => {
    getDepositPayments();
    getRepoDeals();
    getSubAccounts();
  }, []);

  const [questionsRequestModal, setQuestionsRequestModal] = useState(
    location.state?.questionsRequestModal || false,
  );

  const openFileSelector = () => {
    const fileInput = document.getElementById("fileInput") as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      e.target.value = "";
      uploadAvatar(file, err, updateUserAvatar);
    }
  };
  const closeVideo = () => {
    setVideo(false);

    if (videoRef.current) {
      const mediaStream = videoRef.current.srcObject as MediaStream;
      if (mediaStream) {
        const tracks = mediaStream.getTracks();
        tracks.forEach((track) => {
          track.stop();
        });
      }

      videoRef.current.srcObject = null;
    }
  };

  useBlockScrollbar(
    video ||
      editImgModal ||
      languageModal ||
      promoCodeModal ||
      subAccountsModal,
    [video, editImgModal, languageModal, promoCodeModal, subAccountsModal],
  );

  const onUploadAvatar = (image: any) => {
    uploadAvatar(blobToFile(image, `AVATAR.jpg`), err, updateUserAvatar);
  };

  const getUserDefaultImage = () => {
    if (kyc || kyb) {
      if (entity === EntityType.Individual) {
        if (kyc && kyc.gender === "female") {
          return FemaleUser;
        }
      } else {
        return BusinessUser;
      }
    }

    return MaleUser;
  };

  const showSubAccounts =
    user?.signContract &&
    (["LF-1305", "LA-1144"].includes(user?.identificator) ||
      subAccounts.length > 0);

  const accountOptions = [
    ...(showSubAccounts
      ? [
          {
            onClick: () => setSubAccountsModal(true),
            image: UserIcon,
            option:
              t("selectedAccount") +
              ": " +
              (subAccount
                ? "#" + subAccount?.sub_number + " - " + subAccount?.name
                : t("mainAccount")),
          },
        ]
      : []),
    {
      onClick: () => navigate(PATH_LIST.personal),
      image: UserIcon,
      option: t("personalInfo"),
      notification: !user?.isEmailVerified,
    },
    {
      onClick: () => navigate(PATH_LIST.security),
      image: PasswordIcon,
      option: t("security"),
      notification: !user?.hasPinCode,
    },
    ...(kycbStatus === KYC_Status.Success &&
    !isLegal &&
    (!crsData || crsData?.length === 0)
      ? [
          {
            onClick: () => navigate(PATH_LIST.taxInformation),
            image: TaxInformationIcon,
            option: t("taxInformation"),
            notification: !crsData || crsData?.length === 0 ? true : false,
          },
        ]
      : []),
    ...(kycbStatus !== KYC_Status.Success
      ? [
          {
            onClick: () => navigateVerification(),
            image: VerificationIcon,
            option: t("verification"),
            notification: !kycbStatus,
            svgType: "stroke" as const,
          },
        ]
      : []),
    ...(kycbStatus === KYC_Status.Success &&
    questionnaireStatus !== null &&
    questionnaireStatus !== KYC_Status.Success
      ? [
          {
            onClick: () => navigate(PATH_LIST.questions),
            image: IconQuestion,
            option: t("questionnaire"),
            svgType: "stroke" as const,
          },
        ]
      : []),
    ...(kycbStatus === KYC_Status.Success &&
    (!isLegal ? crsData && crsData.length > 0 : true)
      ? [
          {
            onClick: () => {
              if (!user?.hasPinCode) {
                setInactivePinCodeModal(true);
              } else if (!user.isEmailVerified) {
                setInactiveEmailModal(true);
              } else {
                navigate(PATH_LIST.signUserContract);
              }
            },
            image: TextDocIcon,
            option: t("contract"),
            notification:
              kycbStatus == KYC_Status.Success && !user?.signContract,
          },
        ]
      : []),
    ...(showSubAccounts
      ? [
          {
            onClick: () => navigate(PATH_LIST.subAccounts),
            image: UserIcon,
            option: t("subAccounts"),
          },
        ]
      : []),
    ...(kycbStatus === KYC_Status.Success
      ? [
          {
            onClick: () => navigate(PATH_LIST.assignments),
            image: AssignmentsIcon,
            option: t("assignments"),
          },
        ]
      : []),
    ...(depositPaymentsCount > 0 && kycbStatus === KYC_Status.Success
      ? [
          {
            onClick: () => navigate(PATH_LIST.repoTransaction),
            image: RepoIcon,
            option: t("longTermFundsPlacement"),
          },
        ]
      : []),
    ...((repoDeals.length > 0 || repoDealsHistory.length > 0) &&
    kycbStatus === KYC_Status.Success
      ? [
          {
            onClick: () => navigate(PATH_LIST.repoDeals),
            image: RepoIcon,
            option: t("repoTransactions"),
          },
        ]
      : []),
    {
      onClick: () => navigate(PATH_LIST.tariffs),
      image: TextDocIcon,
      option: t("myTariffs"),
    },
    {
      onClick: () => navigate(PATH_LIST.myBonuses),
      image: MyBonuses,
      option: t("bonus"),
    },
    {
      onClick: () => setPromoCodeModal(true),
      image: PromoCode,
      option: t("promoCode"),
    },
    {
      onClick: () => setLanguageModal(true),
      image: LanguageIcon,
      option: t("language"),
    },
  ];

  const depositWithdrawalOptions = [
    {
      onClick: () => navigate(PATH_LIST.deposit),
      image: DepositIcon,
      option: t("deposit"),
    },
    {
      onClick: () => navigate(PATH_LIST.withdrawal),
      image: WithdrawalIcon,
      option: t("withdrawal"),
    },
  ];

  const reportOptions = [
    {
      onClick: () => navigate(PATH_LIST.history),
      image: HistoryIcon,
      option: t("history"),
    },
    {
      onClick: () => navigate(PATH_LIST.brokerageReport),
      image: TextDocIcon,
      option: t("brokerageReports"),
    },
    // navigate(PATH_LIST.taxReport);
  ];

  const {
    navigateVerification,
    verificationRequestModal,
    setVerificationRequestModal,
  } = useVerificationNavigation();

  // console.log(crsData);

  return (
    <div className="account">
      <div className="user-info">
        <div onClick={() => setEditImgModal(true)} className="user-image">
          {user?.avatar ? (
            <img
              className="profile-img profile-img-active"
              src={getCdnUrl(user.avatar, "avatar")}
              alt="User"
            />
          ) : (
            <img
              className="profile-img"
              src={getUserDefaultImage()}
              alt="User icon"
            />
          )}
          <img className="editImg" src={EditImg} alt="EditImage" />
          <input
            id="fileInput"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </div>
        {username?.trim() ? <p> {username}</p> : <p>{t("nameSurname")}</p>}
        <UserStatus status={user?.tariff} />
      </div>

      <p className="acc-verification-status">
        {!kycbStatus
          ? t("yourAccIsNotVerified")
          : kycbStatus == KYC_Status.Pending
          ? t("verificatioRequestReviewed")
          : (kycbStatus == KYC_Status.Success && !crsData) ||
            crsData?.length === 0
          ? v("crs-data")
          : kycbStatus == KYC_Status.Success && !user?.signContract
          ? t("signToMakeTransactions")
          : ""}
      </p>

      <div className="account-options">
        <p className="part-title">{t("account")}</p>
        {accountOptions.map((item, index) => (
          <AccountOptionCard
            key={index}
            onClick={item.onClick}
            image={item.image}
            option={item.option}
            notification={item.notification}
            svgType={item.svgType}
          />
        ))}

        {user?.signContract && !subAccount && (
          <>
            <p className="part-title">{t("deposit&Withdrawal")}</p>
            {depositWithdrawalOptions.map((item, index) => (
              <AccountOptionCard
                key={index}
                onClick={item.onClick}
                image={item.image}
                option={item.option}
              />
            ))}
          </>
        )}

        {user?.signContract && (
          <>
            <p className="part-title">{t("reports")}</p>
            {reportOptions.map((item, index) => (
              <AccountOptionCard
                key={index}
                onClick={item.onClick}
                image={item.image}
                option={item.option}
              />
            ))}
          </>
        )}
      </div>

      {editImgModal === true && (
        <EditProfilePhoto
          onGalleryClick={() => {
            openFileSelector();
            setEditImgModal(false);
          }}
          onCameraClick={() => {
            setVideo(true);
            setEditImgModal(false);
          }}
          onRemoveClick={() => {
            removeAvatar(err, updateUserAvatar);
            setEditImgModal(false);
          }}
          remove={!!user?.avatar}
          onClose={() => setEditImgModal(false)}
        />
      )}
      {languageModal === true && (
        <LanguageModal
          onClose={() => {
            setTimeout(() => {
              setLanguageModal(false);
            }, 200);
          }}
        />
      )}
      {promoCodeModal === true && (
        <PromoCodeModal
          onClose={() => {
            setTimeout(() => {
              setPromoCodeModal(false);
            }, 200);
          }}
        />
      )}
      {video === true && (
        <TakePhotoModal
          className="acc-take-photo"
          onOpen={(image: string) => {
            onUploadAvatar(image);
            closeVideo();
          }}
          onClose={closeVideo}
          type={DocumentType.Avatar}
        ></TakePhotoModal>
      )}
      {subAccountsModal === true && (
        <SubAccountModal
          selectedSubAccount={subAccountSubNumber}
          subAccounts={subAccounts}
          onSelect={(subAccount: string | null) => {
            setSubAccountSubNumber(subAccount);
            setSubAccountsModal(false);
            navigate(PATH_LIST.home);
            setTimeout(() => {
              window.location.reload();
            }, 100);
          }}
          onClose={() => {
            setTimeout(() => {
              setSubAccountsModal(false);
            }, 100);
          }}
        />
      )}

      {verificationRequestModal && (
        <Modal
          icon={VerificationRequest}
          customModal="modalLogOut verificationRequestModal"
          onClick={() => {
            window.history.replaceState({}, "");
            setVerificationRequestModal(false);
            navigate(PATH_LIST.home);
          }}
          onHide={() => {
            window.history.replaceState({}, "");
            setVerificationRequestModal(false);
          }}
          // title={t("verificationRequest")}
          // text={t("verificationRequestSent")}
          title={o("verificationSubmitted")}
          text={o("checkingInfo")}
          showSecondBtn={false}
          customOutlet="customOutlet"
          btnText={o("goToApp")}
          // btnText="Close"
        />
      )}

      {questionsRequestModal && (
        <Modal
          customModal="modalLogOut verificationRequestModal"
          onClick={() => {
            window.history.replaceState({}, "");
            setQuestionsRequestModal(false);
          }}
          onHide={() => {
            window.history.replaceState({}, "");
            setQuestionsRequestModal(false);
          }}
          title={t("questionsModalTitle")}
          text={t("questionsModalText")}
          showSecondBtn={false}
          customOutlet="customOutlet"
        />
      )}
    </div>
  );
};
